import { keyframes } from "@mui/system";
import type { CSSObject, Theme } from "@mui/material/styles";

// App-wide motion tokens, for motion that isn't owned by a single feature.
//
// Values come from the transitions.dev motion scale (/transitions-polish), picked by
// *usage* (modal open/close, text swap, tooltip …), not by nearest number. Where the
// scale and CLAUDE.md disagree, CLAUDE.md wins:
//   - Compositor-only: transform + opacity. The scale's blur tokens are deliberately
//     unused (filter isn't on the list), so every "cross-blur" recipe here is opacity-only.
//   - Entrances use the project entrance curve, not the scale's --ease-smooth-out. The
//     scale's curve is kept for closes and position changes, which CLAUDE.md doesn't cover.
//   - Every animation is guarded for reduced motion (CSS media query + this probe in JS).
//
// The notifications popover keeps its own feature tokens in components/Notifications/motion,
// which re-exports the shared ones below.

// Module-level probe (same pattern as useSwipeToDismiss) so we never re-query per render.
// It's read once, so every JS-driven phase also has a timer fallback: if the OS setting
// flips mid-session the CSS guard kills the animation and the fallback still finishes it.
export const prefersReducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── Easing ────────────────────────────────────────────────────────────────────
/** Project entrance curve (CLAUDE.md). Also the swipe commit's curve in useSwipeToDismiss. */
export const EASE_ENTRANCE = "cubic-bezier(0.16, 1, 0.3, 1)";
/** --ease-smooth-out: closes, hover tints and position changes (FLIP). */
export const EASE_SMOOTH_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
/** --ease-in-out: in-place swaps (text swap, icon swap). */
export const EASE_IN_OUT = "ease-in-out";
/** --ease-out: tooltip open / close. */
export const EASE_OUT = "ease-out";

// ── Modal open / close (transitions-dev "Modal", centred, no anchor) ─────────
/** --duration-fast: modal open. */
export const MODAL_OPEN_MS = 250;
/** --duration-quick: modal close. Closes are shorter so dismissal gets out of the way. */
export const MODAL_CLOSE_MS = 150;
/** --scale-large: the pre-open scale a modal grows from, and dips back to on close. */
export const MODAL_SCALE = 0.96;

// ── Bottom sheet (the phone lane of every popup — a panel sliding into a region) ─
/** --duration-slow: panel open. */
export const SHEET_OPEN_MS = 400;
/** --duration-medium: panel close — shorter than the open, per the open/close asymmetry. */
export const SHEET_CLOSE_MS = 350;

// ── Tooltip (transitions-dev "Tooltip") ──────────────────────────────────────
/** --duration-micro: intent delay, so a passing cursor doesn't raise the bubble. */
export const TOOLTIP_DELAY_MS = 80;
/** --duration-quick: tooltip appear. */
export const TOOLTIP_IN_MS = 150;
/** The recipe's near-instant out (--tt-out-dur). Never delay a dismissal. */
export const TOOLTIP_OUT_MS = 50;
/** --scale-small: tooltip pre-open scale. */
export const TOOLTIP_SCALE = 0.98;

// ── Small swaps and reveals ───────────────────────────────────────────────────
/** --duration-quick (text swap): label ↔ spinner, error line. Symmetric by rule. */
export const TEXT_SWAP_MS = 150;
/** --distance-micro: text swap travel. */
export const TEXT_SWAP_DISTANCE = 4;
/** --duration-fast (icon swap): a spinner arriving in a button. Symmetric by rule. */
export const ICON_SWAP_MS = 250;
/** Icon-swap start scale (transitions-dev icon swap default). */
export const ICON_SWAP_SCALE = 0.25;
/** --duration-fast (page slide): a block of a popup handing over to the next one. */
export const STEP_SWAP_MS = 250;
/** --distance-base: page slide travel. */
export const STEP_SWAP_DISTANCE = 8;

// ── Hover and press ───────────────────────────────────────────────────────────
/** Hover-in: quick and direct (--duration-quick). */
export const HOVER_IN_MS = 150;
/** Hover-out: softer and a touch longer (--duration-fast), never delayed. */
export const HOVER_OUT_MS = 250;
/**
 * Press: a button dips to --scale-medium while held. No token usage names "press", so the
 * dip borrows the dropdown-open scale and the hover-in clock; the release eases back on the
 * hover-out clock like any other return.
 */
export const PRESS_SCALE = 0.97;

// Slack added to timer fallbacks so a phase that started a frame late still finishes
// on its own transitionend/animationend before the timer fires.
export const FRAME_SLACK_MS = 50;

// ── Keyframes (one-shot entrances; closes are transitions, never keyframes) ──

// Text swap / step swap entrance: rise into place from `--swap-distance` below.
const swapIn = keyframes`
  from { opacity: 0; transform: translate3d(0, var(--swap-distance, ${TEXT_SWAP_DISTANCE}px), 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Icon swap entrance: grow out of the centre of the slot.
const iconIn = keyframes`
  from { opacity: 0; transform: scale(${ICON_SWAP_SCALE}); }
  to { opacity: 1; transform: scale(1); }
`;

// Both entrances below fill `backwards`, not `both`: the end frame equals the element's own
// resting style, so holding it would only pin a `transform` (and its compositor layer) on the
// element for good after the swap is over.

/**
 * One-shot entrance for content that replaces other content in place — an error line, a
 * consequence note whose copy just changed, a signing block taking over from a button. Key
 * the element on what it shows so a change remounts it and replays the swap.
 *
 * Defaults to the text-swap lane (150ms, 4px); pass the step-swap values for a larger block.
 */
export const swapInSx = (ms = TEXT_SWAP_MS, distance = TEXT_SWAP_DISTANCE): CSSObject => ({
  "--swap-distance": `${distance}px`,
  animation: `${swapIn} ${ms}ms ${EASE_ENTRANCE} backwards`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

/** One-shot icon-swap entrance for a spinner or glyph appearing in an existing slot. */
export const iconInSx: CSSObject = {
  animation: `${iconIn} ${ICON_SWAP_MS}ms ${EASE_IN_OUT} backwards`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * Hover feedback as an opacity fade on a `::before` tint layer instead of a
 * background-color transition, so the only animated property is opacity.
 *
 * The host must be positioned (Row, and MUI's ButtonBase, already are). `isolation:
 * isolate` makes the host a stacking context, so the layer's `z-index: -1` puts it
 * above the host's own background (e.g. the urgent row tint) but under its content.
 * Hover is gated on `(hover: hover)` so a tap on touch screens doesn't leave a
 * sticky tint behind.
 *
 * The dark tint and the reduced-motion guards are nested *inside* the pseudo-element
 * rules on purpose: without CSS vars, `theme.applyStyles` returns its object as-is,
 * so a top-level spread would replace a host's own `&::before` / media-query keys.
 */
export const hoverTint = (theme: Theme, tint: { light: string; dark: string }): CSSObject => ({
  isolation: "isolate",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    zIndex: -1,
    borderRadius: "inherit",
    pointerEvents: "none",
    backgroundColor: tint.light,
    opacity: 0,
    // Resting rule = hover-out timing; the :hover rule below swaps in the quicker in.
    transition: `opacity ${HOVER_OUT_MS}ms ${EASE_SMOOTH_OUT}`,
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    ...theme.applyStyles("dark", { backgroundColor: tint.dark }),
  },
  "@media (hover: hover)": {
    "&:hover::before": {
      opacity: 1,
      transition: `opacity ${HOVER_IN_MS}ms ${EASE_SMOOTH_OUT}`,
      // Repeated here: this rule is more specific than the resting one.
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    },
  },
});

/**
 * Press feedback: the host dips to PRESS_SCALE while held and eases back on release.
 * Transform only. Resting rule = the release (hover-out clock); `:active` swaps in the
 * quicker press. Under reduced motion there is no dip at all, not just no tween.
 */
export const pressDip: CSSObject = {
  transition: `transform ${HOVER_OUT_MS}ms ${EASE_SMOOTH_OUT}`,
  "&:active": {
    transform: `scale(${PRESS_SCALE})`,
    transition: `transform ${HOVER_IN_MS}ms ${EASE_SMOOTH_OUT}`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:active": { transform: "none" },
  },
};
